import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import api from "@/lib/api";

export default function PaymentResult({ status }) {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [record, setRecord] = useState(null);
    const [error, setError] = useState(null);
    const tries = useRef(0);

    useEffect(() => {
        if (status !== "success" || !sessionId) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { setRecord(data); return; }
                if (data.payment_status === "failed" || data.payment_status === "expired") {
                    setError("Payment did not complete. Please try again.");
                    return;
                }
                tries.current += 1;
                if (tries.current > 20) { setError("Still waiting on confirmation — check your email or try again."); return; }
                setTimeout(poll, 1500);
            } catch (e) {
                setError("Could not fetch payment status.");
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [status, sessionId]);

    if (status === "cancel") {
        return (
            <PublicLayout>
                <section className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-cancel">
                    <XCircle size={48} className="mx-auto text-[hsl(var(--destructive))]" />
                    <h1 className="font-display text-4xl mt-4">Payment cancelled</h1>
                    <p className="mt-4 text-[hsl(var(--muted-foreground))]">No charge was made. Your appointment is still on hold — you can retry payment or reschedule.</p>
                    <Link to="/book" className="pill btn-primary inline-block px-6 py-3 mt-8 text-sm">Try again</Link>
                </section>
            </PublicLayout>
        );
    }

    if (error) {
        return (
            <PublicLayout>
                <section className="max-w-xl mx-auto px-6 py-24 text-center">
                    <XCircle size={48} className="mx-auto text-[hsl(var(--destructive))]" />
                    <h1 className="font-display text-3xl mt-4">Payment issue</h1>
                    <p className="mt-3 text-[hsl(var(--muted-foreground))]">{error}</p>
                    <Link to="/book" className="pill btn-primary inline-block px-6 py-3 mt-6 text-sm">Return to booking</Link>
                </section>
            </PublicLayout>
        );
    }

    if (!record) {
        return (
            <PublicLayout>
                <section className="max-w-xl mx-auto px-6 py-24 text-center">
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Confirming payment</div>
                    <h1 className="font-display text-3xl mt-3">One moment…</h1>
                </section>
            </PublicLayout>
        );
    }

    const appt = record.appointment || {};
    return (
        <PublicLayout>
            <section className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="payment-success">
                <CheckCircle2 size={56} className="mx-auto text-[hsl(var(--success))]" />
                <h1 className="font-display text-4xl mt-4">Payment received</h1>
                <p className="mt-4 text-[hsl(var(--muted-foreground))]">
                    Thank you{appt.name ? `, ${appt.name}` : ""}. Your appointment is confirmed.
                </p>
                <div className="card-clay p-6 mt-8 text-left">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Details</div>
                    <div className="mt-2 text-sm"><strong>Service:</strong> {appt.service_title}</div>
                    <div className="text-sm"><strong>When:</strong> {appt.date} at {appt.time}</div>
                    <div className="text-sm"><strong>Format:</strong> {appt.mode === "online" ? "Online video call" : "In person"}</div>
                    {appt.meeting_url && (
                        <div className="mt-3">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Meeting link</div>
                            <a href={appt.meeting_url} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-[hsl(var(--primary))] underline break-all">
                                {appt.meeting_url}
                            </a>
                        </div>
                    )}
                </div>
                <Link to="/" className="pill btn-primary inline-block px-6 py-3 mt-8 text-sm">Back to home</Link>
            </section>
        </PublicLayout>
    );
}
